import { Check } from 'lucide-react';
import type { StepState } from '@/lib/setupSteps';

interface StepHeaderProps {
  index: number;
  state: StepState;
  title: string;
  description: string;
}

function StatusIndicator({ state, index }: { state: StepState; index: number }) {
  if (state === 'done') {
    return (
      <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-green-600">
        <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />
      </span>
    );
  }
  if (state === 'loading') {
    return <span className="h-6 w-6 flex-none animate-pulse rounded-full bg-muted" />;
  }
  return (
    <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full border-2 border-muted-foreground/30 text-xs font-semibold text-muted-foreground">
      {index}
    </span>
  );
}

/** Shared status-indicator + title/description block used by SetupStep and
 *  CollapsibleSetupStep. */
export function StepHeader({ index, state, title, description }: StepHeaderProps) {
  return (
    <>
      <StatusIndicator state={state} index={index} />
      <div className="flex-1 space-y-2">
        <div>
          <div className="font-semibold text-foreground">{title}</div>
          <div className="mt-0.5 text-sm text-muted-foreground">{description}</div>
        </div>
      </div>
    </>
  );
}

interface SetupStepProps extends StepHeaderProps {
  children?: React.ReactNode;
}

const SetupStep: React.FC<SetupStepProps> = ({ index, state, title, description, children }) => (
  <div className="flex gap-3 border-b border-border p-5 last:border-b-0">
    <StatusIndicator state={state} index={index} />
    <div className="flex-1 space-y-2">
      <div>
        <div className="font-semibold text-foreground">{title}</div>
        <div className="mt-0.5 text-sm text-muted-foreground">{description}</div>
      </div>
      {children}
    </div>
  </div>
);

export default SetupStep;
